import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Save, Loader2, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Per-user "Coach Preferences" — soft signals that nudge the DJ Coach toward
 * the DJ's personal style. Sits on top of the global Coach Profile.
 *
 * Stored at `db.user_coach_prefs` (keyed by user_id) and read by both
 * `_get_dj_chat()` (system prompt) and `/api/dj/build-set` (Phase 2 scorer).
 */
export default function CoachPrefsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bpmMin, setBpmMin] = useState('');
  const [bpmMax, setBpmMax] = useState('');
  const [signature, setSignature] = useState([]);
  const [genreInput, setGenreInput] = useState('');
  const [style, setStyle] = useState('');
  const [notes, setNotes] = useState('');
  const [playingStyle, setPlayingStyle] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dj/coach-prefs`);
        setBpmMin(data?.preferred_bpm_min ?? '');
        setBpmMax(data?.preferred_bpm_max ?? '');
        setSignature(data?.signature_genres || []);
        setStyle(data?.mixing_style || '');
        setNotes(data?.personal_notes || '');
        setPlayingStyle(data?.playing_style || '');
      } catch {
        /* empty prefs — fine */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addGenre = () => {
    const g = genreInput.trim();
    if (!g || signature.includes(g) || signature.length >= 10) return;
    setSignature((s) => [...s, g]);
    setGenreInput('');
  };

  const removeGenre = (g) => setSignature((s) => s.filter((x) => x !== g));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/dj/coach-prefs`, {
        preferred_bpm_min: bpmMin === '' ? null : Number(bpmMin),
        preferred_bpm_max: bpmMax === '' ? null : Number(bpmMax),
        signature_genres: signature,
        mixing_style: style,
        personal_notes: notes,
        playing_style: playingStyle || null,
      });
      toast.success('Assistant preferences saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-pane p-6" data-testid="coach-prefs-loading">
        <Loader2 className="w-5 h-5 text-[#2563FF] mx-auto animate-spin" />
      </div>
    );
  }

  return (
    <div className="glass-pane p-6 space-y-5" data-testid="coach-prefs-card">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-base font-semibold text-[#0F172A]">Assistant Preferences</h3>
          <p className="text-xs text-[#64748B] mt-1 max-w-xl">
            Personal signals the AI Assistant uses to tailor advice to your style.
            Adds on top of the studio's global profile — your brief still wins.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
          data-testid="coach-prefs-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>

      {/* Playing style */}
      <div>
        <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF] mb-2">PLAYING STYLE</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {[
            { key: 'steady', title: 'Steady', desc: 'One tempo all night. Long blends.' },
            { key: 'progressive', title: 'Progressive', desc: 'Gradual BPM/energy climb.' },
            { key: 'chop_and_change', title: 'Chop & Change', desc: 'Varied BPM, surprise key shifts.' },
          ].map((opt) => {
            const active = playingStyle === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setPlayingStyle(active ? '' : opt.key)}
                data-testid={`coach-prefs-playing-${opt.key}`}
                className={`text-left p-3 rounded-xl border transition-all ${
                  active
                    ? 'border-[#2563FF] bg-[#EFF6FF] shadow-[0_4px_14px_rgba(37,99,255,0.18)]'
                    : 'border-[#E2E8F0] bg-white hover:border-[#2563FF]/40'
                }`}
              >
                <p className={`text-sm font-semibold ${active ? 'text-[#2563FF]' : 'text-[#0F172A]'}`}>{opt.title}</p>
                <p className="text-[11px] text-[#64748B] mt-0.5 leading-snug">{opt.desc}</p>
              </button>
            );
          })}
        </div>
        <p className="text-[10px] font-mono text-[#94A3B8] mt-1.5">Used by the assistant as the default energy curve when you ask for a set.</p>
      </div>

      {/* BPM stance */}
      <div>
        <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF] mb-2">PREFERRED BPM STANCE</p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="60"
            max="200"
            placeholder="min"
            value={bpmMin}
            onChange={(e) => setBpmMin(e.target.value)}
            data-testid="coach-prefs-bpm-min"
            className="w-24 px-3 py-2 rounded-xl border border-[#E2E8F0] bg-white text-sm outline-none focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15 font-mono"
          />
          <span className="text-[#94A3B8] font-mono text-sm">–</span>
          <input
            type="number"
            min="60"
            max="200"
            placeholder="max"
            value={bpmMax}
            onChange={(e) => setBpmMax(e.target.value)}
            data-testid="coach-prefs-bpm-max"
            className="w-24 px-3 py-2 rounded-xl border border-[#E2E8F0] bg-white text-sm outline-none focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15 font-mono"
          />
          <span className="text-xs text-[#64748B]">BPM</span>
        </div>
      </div>

      {/* Signature genres */}
      <div>
        <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF] mb-2">SIGNATURE GENRES / LABELS</p>
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="coach-prefs-genre-list">
          {signature.map((g) => (
            <span key={g} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#EFF6FF] border border-[#DBEAFE] text-[#2563FF] text-xs font-medium">
              {g}
              <button
                onClick={() => removeGenre(g)}
                className="hover:text-red-500"
                data-testid={`coach-prefs-genre-remove-${g}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {signature.length === 0 && (
            <span className="text-xs text-[#94A3B8] italic">none yet</span>
          )}
        </div>
        <div className="flex gap-2">
          <input
            value={genreInput}
            onChange={(e) => setGenreInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGenre(); } }}
            placeholder="e.g. melodic techno, Drumcode, Anjunadeep"
            data-testid="coach-prefs-genre-input"
            className="flex-1 px-3 py-2 rounded-xl border border-[#E2E8F0] bg-white text-sm outline-none focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15"
          />
          <button
            onClick={addGenre}
            data-testid="coach-prefs-genre-add"
            className="px-3 py-2 rounded-xl border border-[#E2E8F0] hover:border-[#2563FF]/40 text-[#2563FF] text-sm flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <p className="text-[10px] font-mono text-[#94A3B8] mt-1">Max 10 — these get a soft bonus during Phase 2 set building.</p>
      </div>

      {/* Mixing style */}
      <div>
        <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF] mb-2">MIXING STYLE</p>
        <input
          value={style}
          onChange={(e) => setStyle(e.target.value)}
          maxLength={400}
          placeholder="e.g. long blends, harmonic, no quick cuts"
          data-testid="coach-prefs-style"
          className="w-full px-3 py-2 rounded-xl border border-[#E2E8F0] bg-white text-sm outline-none focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15"
        />
      </div>

      {/* Personal notes */}
      <div>
        <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF] mb-2">PERSONAL NOTES</p>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
          rows={4}
          placeholder="Anything else the assistant should remember — quirks, hooks you love, things to avoid."
          data-testid="coach-prefs-notes"
          className="w-full px-3 py-2 rounded-xl border border-[#E2E8F0] bg-white text-sm outline-none focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/15 font-mono"
        />
        <p className="text-[10px] font-mono text-[#94A3B8] mt-1 text-right">{notes.length}/2000</p>
      </div>
    </div>
  );
}
